import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface AccordionItemProps {
  title: string;
  icon?: React.ReactNode;
  isOpen?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

interface AccordionProps {
  defaultOpen?: number;
  allowMultiple?: boolean;
  children: React.ReactNode;
}

export const AccordionItem: React.FC<AccordionItemProps> = ({ 
  title, 
  icon, 
  isOpen = false, 
  onToggle, 
  children 
}) => {
  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4 bg-white shadow-sm hover:shadow-md transition-shadow">
      <button
        className="w-full p-4 text-left flex items-center justify-between"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <span className="font-semibold text-gray-900">{title}</span>
        </div>
        <ChevronDown
          className={`transition-transform duration-300 text-primary-500 ${
            isOpen ? 'rotate-180' : ''
          }`}
          size={20}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div className="p-4 border-t border-gray-200 bg-gray-50">{children}</div>
      </div>
    </div>
  );
};

const Accordion: React.FC<AccordionProps> = ({ 
  defaultOpen = -1, 
  allowMultiple = false, 
  children 
}) => {
  const [openItems, setOpenItems] = useState<number[]>(
    defaultOpen >= 0 ? [defaultOpen] : []
  );

  const handleToggle = (index: number) => {
    if (allowMultiple) {
      setOpenItems((prev) =>
        prev.includes(index)
          ? prev.filter((item) => item !== index)
          : [...prev, index]
      );
    } else {
      setOpenItems((prev) => (prev.includes(index) ? [] : [index]));
    }
  };

  return (
    <div className="space-y-2">
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement<AccordionItemProps>(child)) {
          return React.cloneElement(child, {
            isOpen: openItems.includes(index),
            onToggle: () => handleToggle(index),
          });
        }
        return child;
      })}
    </div>
  );
};

export default Accordion;